import { useCallback, useState } from 'react'
import type { Edge } from '@xyflow/react'
import type { Node } from '@xyflow/react'
import type { ReactFlowNodeData } from '../../../types/mindmap-components'
import { getDescendantNodeIds } from '../../../utils/collapseUtils'

export interface PendingDeleteState {
  nodeId: string
  nodeLabel: string
  descendants: Array<{ id: string; label: string; category: string }>
}

interface UseNodeDeletionDeps {
  getCurrentNodes: () => Node<ReactFlowNodeData>[]
  edges: Edge[]
  handleDeleteNode: (nodeId: string) => void
}

export function useNodeDeletion({ getCurrentNodes, edges, handleDeleteNode }: UseNodeDeletionDeps) {
  const [pendingDelete, setPendingDelete] = useState<PendingDeleteState | null>(null)

  /**
   * Deletes a node. If the node has children, shows a confirmation modal first.
   * If no children, deletes immediately.
   */
  const handleDeleteNodeWithConfirmation = useCallback(
    (nodeId: string) => {
      const currentNodes = getCurrentNodes()
      const nodeToDelete = currentNodes.find((n) => n.id === nodeId)
      if (!nodeToDelete) {
        return
      }

      // Get all descendant node IDs
      const descendantIds = getDescendantNodeIds(nodeId, edges)

      // If no descendants, delete immediately
      if (descendantIds.length === 0) {
        handleDeleteNode(nodeId)
        return
      }

      // Build descendant info for the confirmation modal
      const descendants = descendantIds.map((id) => {
        const node = currentNodes.find((n) => n.id === id)
        const category = node?.data.category ?? 'unknown'
        const formattedCategory = category.charAt(0).toUpperCase() + category.slice(1)
        const rawLabel = node?.data.label ?? ''
        const label = rawLabel ? rawLabel : `Empty ${formattedCategory}`

        return { id, label, category }
      })

      // Show confirmation modal
      const parentCategory = nodeToDelete.data.category
      const formattedParentCategory =
        parentCategory.charAt(0).toUpperCase() + parentCategory.slice(1)

      const parentRawLabel = nodeToDelete.data.label
      const parentLabel = parentRawLabel ? parentRawLabel : `Empty ${formattedParentCategory}`

      setPendingDelete({
        nodeId,
        nodeLabel: parentLabel,
        descendants,
      })
    },
    [getCurrentNodes, edges, handleDeleteNode]
  )

  /**
   * Confirms deletion - deletes the parent node and all its descendants.
   * Called when user confirms in the delete confirmation modal.
   */
  const confirmDeleteWithChildren = useCallback(() => {
    if (!pendingDelete) return

    const { nodeId, descendants } = pendingDelete

    // Delete all descendant nodes first (from leaves up to avoid orphan issues)
    const reversedDescendants = [...descendants].reverse()
    for (const desc of reversedDescendants) {
      handleDeleteNode(desc.id)
    }

    // Then delete the parent node
    handleDeleteNode(nodeId)

    // Close the modal
    setPendingDelete(null)
  }, [pendingDelete, handleDeleteNode])

  /**
   * Cancels the pending delete operation.
   */
  const cancelDelete = useCallback(() => {
    setPendingDelete(null)
  }, [])

  return {
    handleDeleteNodeWithConfirmation,
    confirmDeleteWithChildren,
    cancelDelete,
    pendingDelete,
  }
}
